/**
 * @deprecated Legacy `/map` wiring — used by `pages/map/legacy.tsx` only.
 * After Stage 4 cutover (per docs/superpowers/specs/2026-05-05-map-redesign-design.md
 * Section 6) this file will be deleted. New work should target `pages/map/v2.tsx`
 * + `lib/map/layers/registry.ts` declarative LAYER_REGISTRY.
 *
 * See: lib/map/README.md для cutover timeline.
 */

/**
 * setupMapLayers — единая инициализация всех source/layer для нового /map.
 *
 * Per spec v1.3 + founder feedback 2026-05-04 v6 — Decoupled Layer Architecture:
 *
 *   [ TOP ]
 *     cloud-mask-layer        ← raster, full tile, optional
 *     fields-outline-layer    ← vector LINE, season-bound, ALWAYS visible
 *     crop-icons-layer        ← symbol (PNG), toggle "Иконка культуры"
 *     field-number-layer      ← symbol (text), toggle "Номер поля"
 *     ndvi-raster-layer       ← raster, mutex (1 of 4)
 *     ndvi-contrast-layer     ← raster, mutex
 *     avg-ndvi-fill-layer     ← vector fill, mutex
 *     uniformity-fill-layer   ← vector fill, mutex
 *     crop-fill-harvested     ← phase=harvested, точки цвета культуры (v6)
 *     crop-fill-growing       ← phase=growing, сплошная заливка цвета культуры
 *     crop-fill-planned       ← phase=planned, полосы цвета культуры
 *     tci-snapshot-layer      ← raster, full tile, optional
 *     basemap                 ← base + (опц) overlay (дороги)
 *   [ BOTTOM ]
 *
 * v6 (2026-05-04 founder): убран отдельный crop-color-fill-layer (solid color
 * без учёта фазы). Все 3 phase-layers теперь рисуются цветом культуры с
 * pattern по статусу — управляются ОДНИМ toggle «По цвету культуры»
 * (cropColorFillOn в useMapStore).
 *
 * Sources создаются ОДИН раз и НИКОГДА не уничтожаются — переключение режима/
 * даты обновляет только URL (`setTiles`) или visibility. Это даёт <50ms mode
 * switch и <100ms date switch (бюджет из spec).
 */
import type {
  Map as MLMap,
  ExpressionSpecification,
  LayerSpecification,
  RasterTileSource,
} from 'maplibre-gl';
import { API_URL } from '@/lib/api';

/** ID's used downstream by sync hooks (single source of truth). */
export const LAYER_IDS = {
  basemapBase: 'base',
  basemapOverlay: 'overlay',
  tciSnapshot: 'tci-snapshot-layer',
  cropFillPlanned: 'crop-fill-planned',
  cropFillGrowing: 'crop-fill-growing',
  cropFillHarvested: 'crop-fill-harvested',
  uniformity: 'uniformity-fill-layer',
  avgNdvi: 'avg-ndvi-fill-layer',
  ndvi: 'ndvi-raster-layer',
  ndviContrast: 'ndvi-contrast-layer',
  gnr: 'gnr-raster-layer',
  fieldsOutline: 'fields-outline-layer',
  fieldHighlight: 'field-highlight-layer',
  fieldsHit: 'fields-hit-layer',
  fieldNumber: 'field-number-layer',
  cropName: 'crop-name-layer',
  fieldArea: 'field-area-layer',
  cropIcons: 'crop-icons-layer',
  cloudMask: 'cloud-mask-layer',
} as const;

export const SOURCE_IDS = {
  fieldsMvt: 'fields-mvt',
  ndviRaster: 'ndvi-raster',
  ndviContrast: 'ndvi-contrast-raster',
  gnr: 'gnr-raster',
  tci: 'tci-raster',
  cloudMask: 'cloud-mask-raster',
} as const;

/** MVT source-layer name (FastAPI MVT generator) — polygon geometry. */
const MVT_SOURCE_LAYER = 'fields_v';
/** MVT centroids source-layer name — point per field, без дубликатов на tile
 *  boundaries. Используется для symbol labels (имя/культура/площадь + crop-icon).
 *  Founder QA 2026-05-05: на полигональном source MapLibre брал centroid
 *  каждого polygon-куска tile → одно поле получало 2-3 подписи. На production
 *  Next.js (frontend/src/app/map/page.tsx) labels всегда сидят на отдельном
 *  centroid layer — backend выдаёт его как второй MVT layer в одном tile. */
const MVT_CENTROIDS_LAYER = 'field_centroids';

/** Origin без credentials.
 *
 * Когда staging закрыт nginx basic-auth gate'ом, location.href после login
 * содержит "stage:password@staging.aikolxoz.com/...". Если MapLibre получает
 * относительный tile URL (`/api/...`) и резолвит через `new URL(rel, location.href)`,
 * результат наследует credentials → MapLibre блокирует с
 * "URL is not valid or contains user credentials".
 *
 * Решение: строим absolute URL вручную через `protocol://host` (без credentials).
 * Browser сам прикрепляет basic-auth header к запросам в same origin —
 * credentials в URL не нужны.
 */
function buildOrigin(): string {
  if (typeof window === 'undefined' || !window.location) return '';
  // window.location.host = "staging.aikolxoz.com" (без user:pass@)
  return `${window.location.protocol}//${window.location.host}`;
}

const ORIGIN_PREFIX = API_URL || buildOrigin();

/** URL builders (single source of truth).
 *
 * Backend endpoints (router prefix /api/v1/ndvi):
 *   - GET /raster/{scene_date}/{index_code}/{z}/{x}/{y}.png — NDVI palette + index variants
 *   - GET /raster/ndvi-contrast/{scene_date}/{z}/{x}/{y}.png — pre-computed per-field stretch
 *   - GET /scene-rgb/{scene_date}/{z}/{x}/{y}.png — TCI true-color satellite snapshot
 *   - GET /cloud-mask/{scene_date}/{z}/{x}/{y}.png — облачная маска (debug)
 *
 * Sprint 2D subagent (commit 4a4a2dc9) построил неправильные пути:
 *   ❌ /raster/{date}/{z}/{x}/{y}.png   (missing index_code)
 *   ❌ /raster/tci/...                  (нужно /scene-rgb/...)
 *   ❌ /raster/cloud-mask/...           (нужно /cloud-mask/... без префикса)
 *
 * Founder QA-фидбек 2026-05-01: "спутниковые продукты не работают, консоль красная" —
 * 4 layers возвращали 404. Чиним под актуальный backend.
 */
/** Scope filter passed to MVT tile URL — добавляет cluster_id/farm_id query params.
 *  Sprint 2026-23 T-A6: расширено до canonical 8 (mirror mapState.ts ScopeType).
 *  appendScopeParam ниже обрабатывает только farm/cluster/brigade — остальные
 *  типы (tenant/holding/company/array/field) → no scope param (backend
 *  применяет default scope из active session). */
export interface MvtScope {
  type:
    | 'tenant'
    | 'holding'
    | 'company'
    | 'cluster'
    | 'farm'
    | 'brigade'
    | 'array'
    | 'field';
  id: string | null;
}

/** Добавить scope query-param к URLSearchParams — общий хелпер для MVT и rasters.
 *
 * AK-71: backend NDVI raster endpoints теперь принимают farm_id/cluster_id/brigade_id
 * для clip'а растров по геометрии scope (экономия traffic, корректный визуальный
 * фокус только на полях выбранного хозяйства). Для type === 'company' (default)
 * scope params не передаём — backend применяет no clip (поведение совместимо).
 */
function appendScopeParam(params: URLSearchParams, scope?: MvtScope | null): void {
  if (!scope || !scope.id) return;
  if (scope.type === 'farm') params.set('farm_id', scope.id);
  else if (scope.type === 'cluster') params.set('cluster_id', scope.id);
  else if (scope.type === 'brigade') params.set('brigade_id', scope.id);
  // 'company' → no params (default = no clip on backend)
}

// Cache-bust counter — bumped при каждой пересборке tiles на frontend
// (на смене scope или date). Backend Cache-Control no-cache, но MapLibre
// держит in-memory cache по URL. Меняем URL → force fresh fetch.
let _mvtCacheBust = 0;
export function bumpMvtCacheBust(): number {
  _mvtCacheBust = Date.now();
  return _mvtCacheBust;
}

function buildMvtUrl(date: string, scope?: MvtScope | null): string {
  const params = new URLSearchParams();
  params.set('date', date);
  appendScopeParam(params, scope);
  // Founder bug 2026-05-04: после backend changes (field_number/crop_name/area_ha)
  // MapLibre держал старый MVT в in-memory cache → labels не показывались.
  // _v param меняется при scope/date change → URL новый → cache miss → fresh fetch.
  if (_mvtCacheBust > 0) params.set('_v', String(_mvtCacheBust));
  return `${ORIGIN_PREFIX}/api/v1/tiles/fields/{z}/{x}/{y}.mvt?${params.toString()}`;
}

/** Builder для NDVI raster endpoints с опциональным scope-clip.
 *
 * basePath examples: 'ndvi/raster', 'ndvi/scene-rgb', 'ndvi/scene-gnr'.
 * indexCode (only for /raster/) — 'ndvi' | 'ndvi_contrast' | 'gnr' и т.п.
 * Если indexCode === null → endpoint сам по себе (scene-rgb/scene-gnr).
 */
function buildScopedRasterUrl(
  basePath: string,
  date: string,
  indexCode: string | null,
  scope?: MvtScope | null,
): string {
  const tail = indexCode
    ? `${basePath}/${date}/${indexCode}/{z}/{x}/{y}.png`
    : `${basePath}/${date}/{z}/{x}/{y}.png`;
  const params = new URLSearchParams();
  appendScopeParam(params, scope);
  const qs = params.toString();
  return qs
    ? `${ORIGIN_PREFIX}/api/v1/${tail}?${qs}`
    : `${ORIGIN_PREFIX}/api/v1/${tail}`;
}

export const tileUrls = {
  fieldsMvt: (date: string, scope?: MvtScope | null) => buildMvtUrl(date, scope),
  ndvi: (date: string, scope?: MvtScope | null) =>
    buildScopedRasterUrl('ndvi/raster', date, 'ndvi', scope),
  ndviContrast: (date: string, scope?: MvtScope | null) => {
    // Founder feedback 2026-05-04: «Контрастный пересчитывается на zoom, надо мгновенно».
    // Pre-computed endpoint читает готовый COG с pyramidal overviews [2,4,8] →
    // rio-tiler сам берёт нужный уровень → мгновенный рендер на любом zoom.
    // Backend имеет встроенный fallback на per-field compute если COG отсутствует
    // (см. ndvi.py:ndvi_contrast_precomputed_tile).
    //
    // Scope-clip: pre-computed COG scene-bound, не farm/cluster-bound. Поля других
    // фермеров под outline filter — не критично (raster под field outlines layer).
    const params = new URLSearchParams();
    appendScopeParam(params, scope);
    const qs = params.toString();
    const base = `${ORIGIN_PREFIX}/api/v1/ndvi/raster/ndvi-contrast/${date}/{z}/{x}/{y}.png`;
    return qs ? `${base}?${qs}` : base;
  },
  gnr: (date: string, scope?: MvtScope | null) =>
    // GNR (Green-NIR-Red false-color composite) — 3-band raster.
    // AK-71: dedicated /scene-gnr/{date}/{z}/{x}/{y}.png endpoint (no index_code в path).
    buildScopedRasterUrl('ndvi/scene-gnr', date, null, scope),
  tci: (date: string, scope?: MvtScope | null) =>
    buildScopedRasterUrl('ndvi/scene-rgb', date, null, scope),
  cloudMask: (date: string, scope?: MvtScope | null) =>
    // Cloud mask — scope-clipped (Founder UAT 2026-05-08 lock-in revert AK-71).
    // Founder explicit: «выбрал хозяйство → ничего постороннего не должно
    // ходить». Backend cloud_mask_tile теперь принимает farm_id/cluster_id/
    // brigade_id и intersect'ит mask с scope hull (см. ndvi.py post-2026-05-08).
    buildScopedRasterUrl('ndvi/cloud-mask', date, null, scope),
};

export interface SetupMapLayersOptions {
  selectedDate: string;
  /**
   * ID layer'а в существующем style, ПЕРЕД которым нужно вставить наши слои.
   * Обычно undefined → addLayer без beforeId, layer попадает наверх.
   * Если в стиле есть, скажем, 'overlay' (HERE дороги) — указываем его, чтобы
   * наши data layers оказались под дорогами.
   */
  beforeId?: string;
}

/**
 * Безопасный addSource/addLayer — пропускает если уже существует.
 * Защищает от двойного init на StrictMode + remount.
 */
function safeAddLayer(map: MLMap, layer: LayerSpecification, beforeId?: string): void {
  if (map.getLayer(layer.id)) return;
  if (beforeId && !map.getLayer(beforeId)) {
    map.addLayer(layer);
  } else {
    map.addLayer(layer, beforeId);
  }
}

/**
 * Главная функция инициализации. Идемпотентна.
 */
export function setupMapLayers(map: MLMap, opts: SetupMapLayersOptions): void {
  const { selectedDate, beforeId } = opts;

  // ---------- SOURCES (persistent, never destroyed) ----------
  if (!map.getSource(SOURCE_IDS.fieldsMvt)) {
    map.addSource(SOURCE_IDS.fieldsMvt, {
      type: 'vector',
      tiles: [tileUrls.fieldsMvt(selectedDate)],
      promoteId: 'id',
      minzoom: 6,
      maxzoom: 16,
    });
  }
  if (!map.getSource(SOURCE_IDS.ndviRaster)) {
    map.addSource(SOURCE_IDS.ndviRaster, {
      type: 'raster',
      tiles: [tileUrls.ndvi(selectedDate)],
      tileSize: 256,
      minzoom: 9,
      maxzoom: 16,
    });
  }
  if (!map.getSource(SOURCE_IDS.ndviContrast)) {
    map.addSource(SOURCE_IDS.ndviContrast, {
      type: 'raster',
      tiles: [tileUrls.ndviContrast(selectedDate)],
      tileSize: 256,
      minzoom: 9,
      maxzoom: 16,
    });
  }
  if (!map.getSource(SOURCE_IDS.gnr)) {
    map.addSource(SOURCE_IDS.gnr, {
      type: 'raster',
      tiles: [tileUrls.gnr(selectedDate)],
      tileSize: 256,
      minzoom: 9,
      maxzoom: 16,
    });
  }
  if (!map.getSource(SOURCE_IDS.tci)) {
    map.addSource(SOURCE_IDS.tci, {
      type: 'raster',
      tiles: [tileUrls.tci(selectedDate)],
      tileSize: 256,
      minzoom: 9,
      maxzoom: 16,
    });
  }
  if (!map.getSource(SOURCE_IDS.cloudMask)) {
    map.addSource(SOURCE_IDS.cloudMask, {
      type: 'raster',
      tiles: [tileUrls.cloudMask(selectedDate)],
      tileSize: 256,
      minzoom: 9,
      maxzoom: 16,
    });
  }

  // ---------- LAYERS (bottom → top, see comment header for stack) ----------

  // 1. TCI snapshot (под border'ами полей, optional)
  safeAddLayer(map, {
    id: LAYER_IDS.tciSnapshot,
    type: 'raster',
    source: SOURCE_IDS.tci,
    paint: { 'raster-opacity': 0.9 },
    layout: { visibility: 'none' },
  }, beforeId);

  // 2-4. Crop fill (unified «По цвету культуры», founder 2026-05-04 v6) —
  //   3 phase-layers, каждый рисует цвет культуры со своим pattern по статусу:
  //     - planned   → полосы цвета культуры на полупрозрачном фоне
  //     - growing   → сплошная заливка цветом культуры (через useCropFillExpressionSync)
  //     - harvested → точки цвета культуры на полупрозрачном фоне (v6: раньше
  //                   были полосы стерни; founder заменил на точки для более
  //                   читабельного отличия от planned)
  //   Default visibility = 'visible' (cropColorFillOn default ON). Управляются
  //   ОДНИМ toggle через useCropFillSync (3 layers одновременно).
  safeAddLayer(map, {
    id: LAYER_IDS.cropFillPlanned,
    type: 'fill',
    source: SOURCE_IDS.fieldsMvt,
    'source-layer': MVT_SOURCE_LAYER,
    filter: ['==', ['get', 'current_phase'], 'planned'],
    paint: {
      'fill-pattern': ['concat', 'crop-stripe-', ['to-string', ['get', 'current_crop_id']]],
      'fill-opacity': 0.65,
    },
    layout: { visibility: 'visible' },
  }, beforeId);

  safeAddLayer(map, {
    id: LAYER_IDS.cropFillGrowing,
    type: 'fill',
    source: SOURCE_IDS.fieldsMvt,
    'source-layer': MVT_SOURCE_LAYER,
    filter: ['==', ['get', 'current_phase'], 'growing'],
    layout: { visibility: 'visible' },
    paint: {
      // Дефолтное выражение — серый. Пересоберётся в useCropFillExpressionSync
      // как только загрузится reference.crops.
      'fill-color': 'rgba(156, 163, 175, 0.3)',
      'fill-opacity': 0.55,
    },
  }, beforeId);

  safeAddLayer(map, {
    id: LAYER_IDS.cropFillHarvested,
    type: 'fill',
    source: SOURCE_IDS.fieldsMvt,
    'source-layer': MVT_SOURCE_LAYER,
    filter: ['==', ['get', 'current_phase'], 'harvested'],
    layout: { visibility: 'visible' },
    paint: {
      // v6 (founder 2026-05-04): pattern crop-dots-{id} — точки цвета культуры.
      // Раньше был crop-stubble-{id} (полосы стерни на чёрном фоне).
      'fill-pattern': ['concat', 'crop-dots-', ['to-string', ['get', 'current_crop_id']]],
      'fill-opacity': 0.75,
    },
  }, beforeId);

  // 5. Uniformity (cv_ndvi vector fill, mutex)
  safeAddLayer(map, {
    id: LAYER_IDS.uniformity,
    type: 'fill',
    source: SOURCE_IDS.fieldsMvt,
    'source-layer': MVT_SOURCE_LAYER,
    paint: {
      'fill-color': [
        'interpolate', ['linear'], ['coalesce', ['get', 'cv_ndvi'], 0],
        0, 'hsl(142, 71%, 45%)',
        0.15, 'hsl(38, 92%, 50%)',
        0.30, 'hsl(0, 84%, 60%)',
      ] as ExpressionSpecification,
      'fill-opacity': 0.7,
    },
    layout: { visibility: 'none' },
  }, beforeId);

  // 6. Avg NDVI (vector fill, mutex)
  safeAddLayer(map, {
    id: LAYER_IDS.avgNdvi,
    type: 'fill',
    source: SOURCE_IDS.fieldsMvt,
    'source-layer': MVT_SOURCE_LAYER,
    paint: {
      'fill-color': [
        'interpolate', ['linear'], ['coalesce', ['get', 'avg_ndvi'], 0],
        0.20, 'hsl(0, 84%, 60%)',
        0.40, 'hsl(38, 92%, 50%)',
        0.65, 'hsl(142, 71%, 45%)',
        0.85, 'hsl(142, 72%, 29%)',
      ] as ExpressionSpecification,
      'fill-opacity': 0.85,
    },
    layout: { visibility: 'none' },
  }, beforeId);

  // 7. NDVI raster (mutex)
  safeAddLayer(map, {
    id: LAYER_IDS.ndvi,
    type: 'raster',
    source: SOURCE_IDS.ndviRaster,
    paint: { 'raster-opacity': 0.85 },
    layout: { visibility: 'none' },
  }, beforeId);

  // 8. NDVI contrast (mutex, default-visible per spec — killer feature)
  safeAddLayer(map, {
    id: LAYER_IDS.ndviContrast,
    type: 'raster',
    source: SOURCE_IDS.ndviContrast,
    paint: { 'raster-opacity': 0.9 },
    layout: { visibility: 'visible' },
  }, beforeId);

  // 8c. GNR (Green-NIR-Red composite, mutex)
  safeAddLayer(map, {
    id: LAYER_IDS.gnr,
    type: 'raster',
    source: SOURCE_IDS.gnr,
    paint: { 'raster-opacity': 0.9 },
    layout: { visibility: 'none' },
  }, beforeId);

  // 8b. Invisible hit-test layer — ВСЕГДА видим, fill-opacity 0.
  //     Покрывает interior всех полей, чтобы click внутри поля попадал в
  //     queryRenderedFeatures даже когда все cropFill-layers скрыты (mode='none').
  //     Без него click hit-testing работает только по тонкой границе outline'а
  //     → попадание мимо в 90% случаев (AK-63 founder QA 2026-05-02).
  safeAddLayer(map, {
    id: LAYER_IDS.fieldsHit,
    type: 'fill',
    source: SOURCE_IDS.fieldsMvt,
    'source-layer': MVT_SOURCE_LAYER,
    // P1 #7 audit 2026-05-04: filter ['has','id'] — sanity check что feature имеет id.
    // Без него click мог попасть на feature без id → FieldDetailPopover crashed на null.id.
    filter: ['has', 'id'] as unknown as ExpressionSpecification,
    paint: {
      'fill-color': '#000000',
      'fill-opacity': 0,
    },
  }, beforeId);

  // 9. Field outline — ВСЕГДА видим, season-bound.
  //    Никакой mode switch / date switch его не трогает.
  //    Цвет: оранжевый (#FF6D00 = production accent, canonical token
  //    --accent-orange см. tokens.css) — хорошо читается на спутнике
  //    и на NDVI растрах. Чёрный (hsl 20 14% 4%) терялся на тёмных растрах
  //    и на тёмных областях спутника (founder QA 2026-05-01). Hex inline
  //    т.к. MapLibre paint expressions не принимают `hsl(var(...))`.
  safeAddLayer(map, {
    id: LAYER_IDS.fieldsOutline,
    type: 'line',
    source: SOURCE_IDS.fieldsMvt,
    'source-layer': MVT_SOURCE_LAYER,
    paint: {
      'line-color': '#FF6D00',
      'line-width': [
        'interpolate', ['linear'], ['zoom'],
        9, 0.8,
        13, 1.6,
        16, 2.4,
      ] as ExpressionSpecification,
      'line-opacity': 0.95,
    },
  }, beforeId);

  // 9b. Highlight выбранного поля (yellow outline). Невидим пока feature
  //     filter не выставлен в '== id <selectedId>' (см. applySelectedFieldHighlight).
  //     Лежит над fieldsOutline, под labels — чтобы яркая жёлтая граница
  //     перекрывала оранжевый дефолт, но не закрывала field_number.
  safeAddLayer(map, {
    id: LAYER_IDS.fieldHighlight,
    type: 'line',
    source: SOURCE_IDS.fieldsMvt,
    'source-layer': MVT_SOURCE_LAYER,
    // Filter '==' id '__none__' гарантирует что layer пуст до user click.
    filter: ['==', ['get', 'id'], '__none__'] as ExpressionSpecification,
    paint: {
      'line-color': '#FFEB3B',
      'line-width': 2.5,
      'line-opacity': 1,
    },
  }, beforeId);

  // 10. Field number labels (toggle) — top line.
  // Source-layer: field_centroids (см. MVT_CENTROIDS_LAYER комментарий выше) —
  // одна точка на поле, чтобы label не дублировался на tile boundaries.
  safeAddLayer(map, {
    id: LAYER_IDS.fieldNumber,
    type: 'symbol',
    source: SOURCE_IDS.fieldsMvt,
    'source-layer': MVT_CENTROIDS_LAYER,
    // Founder feedback 2026-05-02: «Имён полей нет» — labels были видны
    // только с zoom 11 (близко). Снижаем до 9 (средний обзор хозяйства)
    // + interpolate text-size чтобы на дальних zoom'ах было компактно.
    minzoom: 9,
    layout: {
      'text-field': ['coalesce', ['get', 'field_number'], ''],
      // Founder QA 2026-05-02: labels не показывались на staging /map.
      // Root cause: demotiles.maplibre.org НЕ хостит "Open Sans Regular" и
      // "Arial Unicode MS Regular" (оба → 404). Из всех font'ов на этом CDN
      // работает только "Noto Sans Bold" — production frontend/ его и использует.
      // Без валидного font glyph'а MapLibre молча не рендерит symbol layer.
      'text-font': ['Noto Sans Bold'],
      'text-size': [
        'interpolate', ['linear'], ['zoom'],
        9, 10,
        12, 12,
        16, 14,
      ] as ExpressionSpecification,
      'text-anchor': 'center',
      'text-justify': 'center',
      'symbol-placement': 'point',
      // Сдвигаем номер поля выше, чтобы под ним поместились название культуры
      // и/или площадь. Offset в em units (привязан к font-size).
      'text-offset': [0, -1.6] as unknown as ExpressionSpecification, // F-N (top)
      // padding=2px вместо default 2 → text не отбрасывается при overlap-detection.
      'text-padding': 2,
      'text-allow-overlap': false, // dedupe across tile fragments
      visibility: 'visible',
    },
    paint: {
      'text-color': '#FFFFFF',
      'text-halo-color': '#000000',
      'text-halo-width': 2,
      'text-halo-blur': 0.2,
    },
  }, beforeId);

  // 10b. Crop name label (toggle, founder 2026-05-04) — middle line.
  //   Текстовая подпись с человеческим именем культуры (например «Пшеница озимая»).
  //   Параллельно с PNG-иконкой (cropIcons) — иконка не всегда читается, текст
  //   даёт точную идентификацию.
  //   Source-layer: field_centroids (один point per field).
  safeAddLayer(map, {
    id: LAYER_IDS.cropName,
    type: 'symbol',
    source: SOURCE_IDS.fieldsMvt,
    'source-layer': MVT_CENTROIDS_LAYER,
    minzoom: 11,
    filter: ['has', 'crop_name'] as unknown as ExpressionSpecification,
    layout: {
      'text-field': ['coalesce', ['get', 'crop_name'], ''],
      'text-font': ['Noto Sans Bold'],
      'text-size': [
        'interpolate', ['linear'], ['zoom'],
        11, 9,
        13, 11,
        16, 13,
      ] as ExpressionSpecification,
      'text-anchor': 'center',
      'text-justify': 'center',
      'symbol-placement': 'point',
      'text-offset': [0, 0.0] as unknown as ExpressionSpecification, // crop name (middle)
      'text-padding': 2,
      // Founder QA 2026-05-05 v2: на field_centroids source-layer overlap=true
      // НЕ нужен — каждое поле имеет ровно 1 point feature, дубликатов на
      // tile boundaries не возникает. Возвращаем allow-overlap=true только для
      // того чтобы middle/bottom строки (cropName, fieldArea) рендерились
      // поверх top строки (fieldNumber) с разным text-offset — иначе
      // collision detection всё равно их отбрасывает (тот же centroid).
      'text-allow-overlap': true,
      'icon-allow-overlap': true,
      'text-max-width': 8,
      visibility: 'none',
    },
    paint: {
      'text-color': '#FFFFFF',
      'text-halo-color': '#000000',
      'text-halo-width': 2,
      'text-halo-blur': 0.2,
    },
  }, beforeId);

  // 10c. Field area label (toggle, founder 2026-05-04) — bottom line.
  //   «124 га» — округлённая area_ha в гектарах. Помогает сразу оценить размер
  //   поля без клика.
  //   Source-layer: field_centroids (один point per field).
  safeAddLayer(map, {
    id: LAYER_IDS.fieldArea,
    type: 'symbol',
    source: SOURCE_IDS.fieldsMvt,
    'source-layer': MVT_CENTROIDS_LAYER,
    minzoom: 11,
    filter: ['has', 'area_ha'] as unknown as ExpressionSpecification,
    layout: {
      'text-field': [
        'concat',
        // round(area_ha) — целое число гектаров
        ['to-string', ['round', ['get', 'area_ha']]],
        ' га',
      ] as ExpressionSpecification,
      'text-font': ['Noto Sans Bold'],
      'text-size': [
        'interpolate', ['linear'], ['zoom'],
        11, 9,
        13, 10,
        16, 12,
      ] as ExpressionSpecification,
      'text-anchor': 'center',
      'text-justify': 'center',
      'symbol-placement': 'point',
      'text-offset': [0, 1.6] as unknown as ExpressionSpecification, // 31 га (bottom)
      'text-padding': 2,
      // Founder QA 2026-05-05 v2: см. cropName layer выше — на field_centroids
      // overlap=true позволяет stacked secondary label поверх fieldNumber.
      'text-allow-overlap': true,
      'icon-allow-overlap': true,
      visibility: 'none',
    },
    paint: {
      'text-color': '#FFFFFF',
      'text-halo-color': '#000000',
      'text-halo-width': 2,
      'text-halo-blur': 0.2,
    },
  }, beforeId);

  // 11. Crop icons (default on — иконка культуры в центре поля)
  // Source-layer: field_centroids (один point per field, без дублей).
  safeAddLayer(map, {
    id: LAYER_IDS.cropIcons,
    type: 'symbol',
    source: SOURCE_IDS.fieldsMvt,
    'source-layer': MVT_CENTROIDS_LAYER,
    // Founder feedback 2026-05-02: «Текущей культуры нет» — снижаем minzoom
    // 12 → 10 чтобы культура была видна на средних обзорах.
    minzoom: 10,
    // filter: рендерим иконки только если у поля есть current_crop_id —
    // иначе concat('crop-icon-', null) = 'crop-icon-' → unknown image.
    filter: ['has', 'current_crop_id'] as unknown as ExpressionSpecification,
    layout: {
      'icon-image': [
        'concat', 'crop-icon-', ['to-string', ['get', 'current_crop_id']],
      ] as ExpressionSpecification,
      'icon-size': [
        'interpolate', ['linear'], ['zoom'],
        10, 0.35,
        12, 0.5,
        16, 0.9,
      ] as ExpressionSpecification,
      // Founder QA 2026-05-02: иконки не появлялись на zoom 11 — `false`
      // отбрасывал большинство при collision-detection с field_number labels.
      // Production /map использует allow-overlap=true для подобных layer'ов.
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
      'symbol-placement': 'point',
      // Default visible — useCropIconsToggleSync hook переопределит из store
      // (теперь cropIconsOn default = true), но на init layer показан сразу,
      // чтобы первый рендер не моргал «выключенно→включенно».
      visibility: 'visible',
    },
  }, beforeId);

  // 12. Cloud mask (поверх всего, alpha-aware overlay).
  //     Backend (AK-pipe-D Phase 1, commit ad17ff32) теперь рендерит cloud-mask
  //     PNG с per-pixel alpha 0..254 (0 = clear, 254 = fully cloud, 255 = no-data
  //     transparent). Edges получают полупрозрачный фиолетовый автоматически —
  //     MapLibre respect'ит alpha channel из PNG.
  //     `raster-opacity` ОБЯЗАТЕЛЬНО 1.0 — иначе MapLibre перемножит fade'нутые
  //     edges (alpha ~30/254) на 0.6 и они станут невидимыми. Confidence-v2
  //     полагается на native alpha без дополнительного multiply.
  //     Response header `X-Cloud-Mask-Version: confidence-v2` подтверждает
  //     версию backend renderer'а.
  safeAddLayer(map, {
    id: LAYER_IDS.cloudMask,
    type: 'raster',
    source: SOURCE_IDS.cloudMask,
    paint: { 'raster-opacity': 1.0 },
    layout: { visibility: 'none' },
  }, beforeId);
}

/**
 * Обновить URL у raster source для новой даты или scope — НЕ пересоздавая source.
 * Используется в useDateSync hook (date change) и в MapCanvas (scope change).
 *
 * AK-71: scope (farm_id/cluster_id/brigade_id) пробрасывается во все raster URLs
 * кроме cloudMask — backend выполняет clip растров по геометрии scope, что
 * экономит traffic и фокусирует визуализацию на полях выбранного хозяйства.
 *
 * cloudMask остаётся без scope (full scene) — founder requirement: облачность
 * нужно видеть в контексте всей сцены, иначе теряется погодный контекст.
 */
export function updateRasterTileUrls(map: MLMap, date: string, scope?: MvtScope | null): void {
  const updates: Array<[string, string]> = [
    [SOURCE_IDS.fieldsMvt, tileUrls.fieldsMvt(date, scope)],
    [SOURCE_IDS.ndviRaster, tileUrls.ndvi(date, scope)],
    [SOURCE_IDS.ndviContrast, tileUrls.ndviContrast(date, scope)],
    [SOURCE_IDS.gnr, tileUrls.gnr(date, scope)],
    [SOURCE_IDS.tci, tileUrls.tci(date, scope)],
    [SOURCE_IDS.cloudMask, tileUrls.cloudMask(date)], // no scope (full scene)
  ];
  for (const [srcId, url] of updates) {
    const src = map.getSource(srcId) as
      | (RasterTileSource & { setTiles?: (t: string[]) => void })
      | undefined;
    src?.setTiles?.([url]);
  }
}

/**
 * Layers (всех типов), которые висят на fields source и должны учитывать
 * scope-фильтр на client-side. Outline + crop-fill (3 фазы) + uniformity + avgNdvi.
 * Labels (cropIcon/fieldNumber) — на тех же features, фильтр тоже нужен.
 */
const SCOPED_FIELD_LAYERS = [
  LAYER_IDS.fieldsOutline,
  LAYER_IDS.fieldsHit,
  LAYER_IDS.cropFillPlanned,
  LAYER_IDS.cropFillGrowing,
  LAYER_IDS.cropFillHarvested,
  LAYER_IDS.uniformity,
  LAYER_IDS.avgNdvi,
  LAYER_IDS.cropIcons,
  LAYER_IDS.cropName,
  LAYER_IDS.fieldArea,
  LAYER_IDS.fieldNumber,
];

/** Сохранённые «базовые» filter'ы по слоям — нужны чтобы scope-фильтр
 * накладывался поверх существующих (current_phase для crop-fill etc.) и
 * корректно снимался когда выбран company. */
const BASE_FILTERS: Record<string, ExpressionSpecification | undefined> = {
  [LAYER_IDS.cropFillPlanned]: ['==', ['get', 'current_phase'], 'planned'],
  [LAYER_IDS.cropFillGrowing]: ['==', ['get', 'current_phase'], 'growing'],
  [LAYER_IDS.cropFillHarvested]: ['==', ['get', 'current_phase'], 'harvested'],
  // cropIcons / cropName рендерятся только для полей с активной культурой —
  // без crop_id concat-expression резолвится в пустоту, crop_name тоже
  // отсутствует.
  [LAYER_IDS.cropIcons]: ['has', 'current_crop_id'] as ExpressionSpecification,
  [LAYER_IDS.cropName]: ['has', 'crop_name'] as ExpressionSpecification,
  [LAYER_IDS.fieldArea]: ['has', 'area_ha'] as ExpressionSpecification,
};

/**
 * Применить scope-фильтр к field-layers (outline + fill + labels). Comбинируется
 * с базовыми filter'ами через ['all', ...]. Если scope === null или type === company —
 * filter сбрасывается к BASE_FILTERS[id] (или undefined если базы нет).
 *
 * Это страховка поверх server-side filter'а в MVT URL: даже если бэкенд
 * вернул лишние features (cache, etc.) — MapLibre их не отрисует.
 */
export function applyScopeFilter(map: MLMap, scope?: MvtScope | null): void {
  const scopeExpr =
    scope && scope.id
      ? scope.type === 'farm'
        ? (['==', ['get', 'farm_id'], scope.id] as ExpressionSpecification)
        : scope.type === 'cluster'
          ? (['==', ['get', 'cluster_id'], scope.id] as ExpressionSpecification)
          : null
      : null;

  for (const id of SCOPED_FIELD_LAYERS) {
    if (!map.getLayer(id)) continue;
    const base = BASE_FILTERS[id];
    let filter: ExpressionSpecification | undefined;
    if (scopeExpr && base) {
      filter = ['all', base, scopeExpr] as ExpressionSpecification;
    } else if (scopeExpr) {
      filter = scopeExpr;
    } else if (base) {
      filter = base;
    } else {
      filter = undefined;
    }
    try {
      // setFilter(undefined) snimaет filter — sup ported in maplibre-gl 3.x.
      map.setFilter(id, filter ?? null);
    } catch (e) {
      if (import.meta.env.DEV) console.warn('[map/scope] setFilter failed for', id, e);
    }
  }
}

/**
 * Пересоздать `fields` MVT source с новым URL (cluster_id/farm_id-фильтр).
 * Production-pattern: setTiles в MapLibre 3.x не выкидывает закешированные in-memory
 * тайлы — нужно полностью удалить и пересоздать source. Layer specs сохраняем
 * и переаппендим, чтобы выбранные filter/paint/layout у crop-fill / outline / labels
 * не сбросились.
 */
export function recreateFieldsSource(map: MLMap, date: string, scope?: MvtScope | null): void {
  // Force MapLibre cache invalidation — bump _mvtCacheBust перед buildMvtUrl
  bumpMvtCacheBust();
  const url = tileUrls.fieldsMvt(date, scope);
  const styleObj = map.getStyle();
  const fieldsLayers = (styleObj.layers ?? []).filter(
    (l) => 'source' in l && (l as { source?: string }).source === SOURCE_IDS.fieldsMvt,
  );
  const layerSpecs = fieldsLayers.map((l) => ({ ...l }));
  for (const l of fieldsLayers) {
    if (map.getLayer(l.id)) map.removeLayer(l.id);
  }
  if (map.getSource(SOURCE_IDS.fieldsMvt)) {
    map.removeSource(SOURCE_IDS.fieldsMvt);
  }
  map.addSource(SOURCE_IDS.fieldsMvt, {
    type: 'vector',
    tiles: [url],
    promoteId: 'id',
    minzoom: 6,
    maxzoom: 16,
  });
  for (const spec of layerSpecs) {
    map.addLayer(spec as LayerSpecification);
  }
}

/** Подсветить выбранное поле (yellow outline). Передать null — снять выделение.
 *
 * Filter переключается на ['==', ['get', 'id'], <id>]; при null —
 * sentinel ('__none__') который ни одна feature не матчит → layer пуст.
 */
export function applySelectedFieldHighlight(map: MLMap, fieldId: string | null): void {
  if (!map.getLayer(LAYER_IDS.fieldHighlight)) return;
  const filter: ExpressionSpecification =
    fieldId == null
      ? (['==', ['get', 'id'], '__none__'] as ExpressionSpecification)
      : (['==', ['get', 'id'], fieldId] as ExpressionSpecification);
  try {
    map.setFilter(LAYER_IDS.fieldHighlight, filter);
  } catch {
    // map уничтожается / стиль ещё не загружен — silent
  }
}
